import { Box, Loader } from '@mantine/core'
import { useState } from 'react'

export interface CrosspointProps {
  sourceId: string
  destinationId: string
  connected: boolean
  loading?: boolean
  error?: boolean
  onClick: () => void
  onHover: (hovered: boolean) => void
  /**
   * When true, the crosspoint involves on-air content (its source feeds an
   * on-air destination, or its destination is itself on-air). The UI shows a
   * red corner badge; the click handler upstream is responsible for asking
   * for confirmation before applying.
   */
  onAirWarning?: boolean
}

export function Crosspoint({
  sourceId,
  destinationId,
  connected,
  loading = false,
  error = false,
  onClick,
  onHover,
  onAirWarning = false
}: CrosspointProps) {
  const [isHovered, setIsHovered] = useState(false)

  const handleMouseEnter = () => {
    setIsHovered(true)
    onHover(true)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    onHover(false)
  }

  const handleClick = () => {
    if (!loading) {
      onClick()
    }
  }

  // Determine visual state using enhanced broadcast theme colors
  const getBackgroundColor = () => {
    if (error) return 'var(--mantine-color-error-1)' // Light red background
    if (loading) return 'var(--mantine-color-loading-1)' // Light amber background
    if (connected) return 'var(--mantine-color-connected-1)' // Light green background
    return 'var(--mantine-color-disconnected-0)' // Very light gray background
  }

  const getBorderColor = () => {
    if (error) return 'var(--mantine-color-error-5)' // Strong red border
    if (loading) return 'var(--mantine-color-loading-5)' // Amber border
    if (connected) return 'var(--mantine-color-connected-5)' // Strong green border
    if (isHovered) return 'var(--mantine-color-blue-4)' // Blue hover border
    return 'var(--mantine-color-disconnected-3)' // Gray border
  }

  const getBoxShadow = () => {
    if (isHovered) return 'var(--mantine-shadow-sm)'
    if (error) return '0 0 0 1px var(--mantine-color-error-3)'
    if (loading) return '0 0 0 1px var(--mantine-color-loading-3)'
    if (connected) return '0 0 0 1px var(--mantine-color-connected-3)'
    return 'none'
  }

  const getCursor = () => {
    if (loading) return 'wait'
    return 'pointer'
  }

  // Generate accessible label
  const getAriaLabel = () => {
    const state = error ? 'error' : loading ? 'loading' : connected ? 'connected' : 'disconnected'
    return `Crosspoint ${sourceId} to ${destinationId}, ${state}`
  }

  return (
    <Box
      data-testid={`crosspoint-${sourceId}-${destinationId}`}
      data-connected={connected}
      data-loading={loading}
      data-error={error}
      data-hovered={isHovered}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="gridcell"
      tabIndex={0}
      aria-label={getAriaLabel()}
      aria-pressed={connected}
      aria-invalid={error}
      aria-busy={loading}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
      style={{
        width: '32px',
        height: '32px',
        backgroundColor: getBackgroundColor(),
        border: `2px solid ${getBorderColor()}`,
        borderRadius: 'var(--mantine-radius-sm)',
        cursor: getCursor(),
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth easing for broadcast-quality transitions
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        transform: isHovered ? 'scale(1.05)' : 'scale(1)', // Subtle scale on hover
        boxShadow: getBoxShadow(),
        zIndex: isHovered ? 10 : 1,
        // Performance optimizations for smooth animations
        willChange: 'transform, box-shadow, background-color, border-color',
        backfaceVisibility: 'hidden', // Prevent flickering during animations
        // Enhanced accessibility with visible focus indicator
        outline: 'none'
      }}
      // Add focus styles via CSS-in-JS for better accessibility
      onFocus={(e) => {
        e.currentTarget.style.outline = '3px solid var(--mantine-color-blue-5)'
        e.currentTarget.style.outlineOffset = '2px'
      }}
      onBlur={(e) => {
        e.currentTarget.style.outline = 'none'
        e.currentTarget.style.outlineOffset = '0'
      }}
    >
      {loading && (
        <Loader 
          size="xs" 
          color={error ? 'red' : connected ? 'green' : 'blue'} 
        />
      )}
      {!loading && connected && (
        <Box
          style={{
            width: '14px',
            height: '14px',
            backgroundColor: 'var(--mantine-color-connected-6)', // Strong green dot
            borderRadius: '50%',
            boxShadow: '0 0 0 1px var(--mantine-color-connected-7)', // Subtle inner shadow
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth broadcast-quality transitions
            transform: isHovered ? 'scale(1.1)' : 'scale(1)', // Subtle scale on hover
            willChange: 'transform, background-color, box-shadow'
          }}
        />
      )}
      {!loading && !connected && !error && (
        <Box
          style={{
            width: '10px',
            height: '10px',
            backgroundColor: 'var(--mantine-color-disconnected-4)', // Gray dot
            borderRadius: '50%',
            opacity: isHovered ? 0.8 : 0.4,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth broadcast-quality transitions
            border: `1px solid var(--mantine-color-disconnected-5)`,
            transform: isHovered ? 'scale(1.2)' : 'scale(1)', // More pronounced scale for disconnected state
            willChange: 'transform, opacity, background-color'
          }}
        />
      )}
      {!loading && error && (
        <Box
          style={{
            width: '14px',
            height: '14px',
            backgroundColor: 'var(--mantine-color-error-6)', // Strong red square
            borderRadius: '2px',
            transform: `rotate(45deg) ${isHovered ? 'scale(1.1)' : 'scale(1)'}`, // Combined transforms
            boxShadow: '0 0 0 1px var(--mantine-color-error-7)', // Subtle inner shadow
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', // Smooth broadcast-quality transitions
            willChange: 'transform, background-color, box-shadow'
          }}
        />
      )}
      {onAirWarning && (
        // Red corner badge indicating an on-air lock. Sits in the top-right
        // of the crosspoint regardless of connection state.
        <Box
          aria-label="on-air warning"
          data-testid={`on-air-badge-${sourceId}-${destinationId}`}
          style={{
            position: 'absolute',
            top: -2,
            right: -2,
            width: 10,
            height: 10,
            backgroundColor: 'var(--mantine-color-red-7)',
            borderRadius: 2,
            boxShadow: '0 0 0 1px white',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  )
}